import { useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Accordion, Button, Heading, Switch, Tooltip } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { Section, Session } from "@models/ISchema";
import { Week } from "@modules/sections/ISections";
import { EditedSession } from "../../context/formData.reducer";
import { useSectionPastSessions } from "../../hooks/useSectionPastSessions";
import SessionCard from "../session-card";
import ScheduledBlocksBadge from "../scheduled-blocks-badge";
import SessionTableTooltip from "../session-table-tooltip";
import { getSectionAssignedBlocks } from "@utils/scheduledBlocks";
import styles from "./sessionsAccordion.module.scss";

interface SessionsAccordionProps {
  section: Section;
  colorIndex?: number;
  selectedSessions?: (Session | EditedSession)[];
  savedSessions?: EditedSession[];
  newSessions?: EditedSession[];
  mockSessionsToCreate?: EditedSession[];
  linkWeeks?: Week[];
  sessionsToDelete?: { [key: string]: EditedSession };
  canCreateSessions?: boolean;
  isCreatingSession?: boolean;
  showScheduledBlocks?: boolean;
  blockEndOptions?: { value?: string }[];
  onSessionClick?: (session: EditedSession, type: "new" | "cloned" | "saved") => void;
  onCreateSession?: () => void;
  onChangeSelectAll?: (
    sessions: Session[],
    section: Section,
    newSessions: EditedSession[],
    checked: boolean,
  ) => void;
}

/**
 * Formats the sessions by merging saved sessions with the current sessions.
 *
 * @param savedSessionsById - An object where the key is the session ID and the value is the edited session.
 * @param sessions - An array of session objects to be formatted.
 * @returns An array of formatted session objects. If a session exists in savedSessionsById, it will be used; otherwise, the original session will be used.
 */
const formatSessions = (savedSessionsById: { [key: string]: EditedSession }, sessions: Session[]) =>
  sessions.map(session =>
    savedSessionsById?.[session?.id]
      ? savedSessionsById[session?.id]
      : { ...session, id: session.id, session },
  );

const getSessionsById = (sessions: (Session | EditedSession)[]) => {
  const formattedSessions: { [key: string]: Session | EditedSession } = {};

  sessions.forEach(session => {
    formattedSessions[session.id] = session;
  });

  return formattedSessions;
};

const SessionsAccordion = ({
  section,
  colorIndex,
  savedSessions = [],
  newSessions = [],
  selectedSessions = [],
  mockSessionsToCreate = [],
  linkWeeks = [],
  sessionsToDelete = {},
  canCreateSessions,
  isCreatingSession,
  showScheduledBlocks,
  blockEndOptions = [],
  onSessionClick,
  onCreateSession,
  onChangeSelectAll,
}: SessionsAccordionProps) => {
  const savedSessionsById = useMemo(() => getSessionsById(savedSessions), [savedSessions]);
  const selectedSessionsById = useMemo(() => getSessionsById(selectedSessions), [selectedSessions]);

  const { t } = useTranslation();

  const { pastSessionIds, hasPastAndActiveSessions, showPastSessions, toggleShowPastSessions } =
    useSectionPastSessions(section);

  const sessions = useMemo(() => {
    return formatSessions(savedSessionsById, [
      ...(section?.sessions ?? []),
      ...(section?.unasignedSessions ?? []),
    ]);
  }, [section, savedSessionsById]);

  const { activeSessions, pastSessions } = useMemo(() => {
    if (!hasPastAndActiveSessions) return { activeSessions: sessions, pastSessions: [] };

    const active: typeof sessions = [];
    const past: typeof sessions = [];

    sessions.forEach(session => {
      (pastSessionIds.has(session.id) ? past : active).push(session);
    });

    return { activeSessions: active, pastSessions: past };
  }, [sessions, pastSessionIds, hasPastAndActiveSessions]);

  const hasHiddenSessions = hasPastAndActiveSessions && !showPastSessions;

  const assignedBlocks = useMemo(
    () =>
      getSectionAssignedBlocks(
        section,
        savedSessions,
        newSessions,
        sessionsToDelete,
        blockEndOptions,
      ),
    [section, savedSessions, newSessions, sessionsToDelete, blockEndOptions],
  );

  const plannedBlocks = section?.courseComponent?.scheduledBlocksCount ?? null;

  const title = `${section?.component?.code} ${section?.code}`;

  const visibleSessions = showPastSessions ? sessions : activeSessions;

  const hasAllSessionsSelected = useMemo(() => {
    if (!section || !(visibleSessions ?? [])?.length) return false;

    const selectedSessionsIds = [...(selectedSessions ?? [])].map(session => session.id);

    return visibleSessions.every(session => selectedSessionsIds.includes(session.id));
  }, [section, selectedSessions, visibleSessions]);

  const renderSessionCard = (session: (typeof sessions)[number]) => (
    <SessionCard
      key={session.id}
      colorIndex={colorIndex}
      session={session}
      linkWeeks={linkWeeks}
      isSelected={!!selectedSessionsById?.[session?.id]}
      isUpdated={!!savedSessionsById?.[session?.id]}
      isDeleted={!!sessionsToDelete?.[session?.id]}
      onClick={session => onSessionClick?.(session, "saved")}
    />
  );

  const handleChangeSelectAll = (checked: boolean) => {
    const sectionSessions = [...section.sessions, ...section.unasignedSessions];
    const visibleSectionSessions = hasHiddenSessions
      ? sectionSessions.filter(session => !pastSessionIds.has(session.id))
      : sectionSessions;
    const savedNewSessionsObj = {};

    [...savedSessions, ...newSessions]?.forEach(value => {
      if (
        value?.section?.id === section.id &&
        (value?.isNew || value?.isCloned) &&
        !savedNewSessionsObj[value?.id]
      ) {
        savedNewSessionsObj[value?.id] = value;
      }
    });

    onChangeSelectAll?.(
      visibleSectionSessions,
      section,
      Object.values(savedNewSessionsObj),
      !checked,
    );
  };

  return (
    <Accordion
      className={cx(styles.sessionsAccordion, {
        [styles.sessionsAccordion__hasBlocksCount]: !!plannedBlocks,
      })}
      // The title is rendered in customContent because Accordion only accepts a string title
      title=""
      opened
      hasCheckbox
      checkboxChecked={hasAllSessionsSelected}
      onChanceCheckbox={e => handleChangeSelectAll(e.target.checked)}
      customContent={
        <>
          <SessionTableTooltip label={title}>
            <Heading type="h3">{title}</Heading>
          </SessionTableTooltip>

          {showScheduledBlocks && (
            <ScheduledBlocksBadge
              className={styles.scheduledBlocksBadge}
              assigned={assignedBlocks}
              planned={plannedBlocks}
            />
          )}
        </>
      }
      actionContent={
        hasHiddenSessions ? (
          <Tooltip
            label={t("assignation-edit.past-sessions.hidden-sessions-tooltip")}
            placement="top"
          >
            <Icon name="circle-info" color="neutral-70" />
          </Tooltip>
        ) : undefined
      }
    >
      <div className={styles.sessionsAccordion_content}>
        {(!!sessions?.length || !!newSessions?.length || !!mockSessionsToCreate?.length) && (
          <div className={styles.sessionsList}>
            {activeSessions.map(renderSessionCard)}

            {newSessions.map(session => (
              <SessionCard
                key={`new-${section?.id}-${session?.id}`}
                colorIndex={colorIndex}
                session={session}
                isSelected={!!selectedSessionsById?.[session?.id]}
                onClick={session => onSessionClick?.(session, "new")}
              />
            ))}

            {mockSessionsToCreate.map(session => (
              <SessionCard
                key={`mock-${section?.id}-${session?.id}`}
                colorIndex={colorIndex}
                session={session}
                isSelected={true}
                isMock
              />
            ))}

            {hasPastAndActiveSessions && (
              <Switch
                className={styles.pastSessionsToggle}
                checked={showPastSessions}
                onChange={toggleShowPastSessions}
                label={t("assignation-edit.past-sessions.toggle-sessions-label")}
              />
            )}

            {hasPastAndActiveSessions && showPastSessions && pastSessions.map(renderSessionCard)}
          </div>
        )}

        {canCreateSessions && (
          <Button
            className={styles.addBtn}
            size="sm"
            variant="ghost"
            disabled={isCreatingSession}
            onClick={onCreateSession}
          >
            {t("assignation-edit.sessions-list.create-session")}
          </Button>
        )}
      </div>
    </Accordion>
  );
};

export default SessionsAccordion;
